import { useEffect, useRef, useState } from "react";
import { RotateCw, Crop, Maximize, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import { warp, rotate90, detectCorners } from "../lib/perspective";
import { Button } from "./ui/button";

const INSET = [[0.06, 0.04], [0.94, 0.04], [0.94, 0.96], [0.06, 0.96]];
const LABELS = ["top-left", "top-right", "bottom-right", "bottom-left"];

const toCanvas = async (file) => {
  const bmp = await createImageBitmap(file);
  const c = document.createElement("canvas");
  c.width = bmp.width;
  c.height = bmp.height;
  c.getContext("2d").drawImage(bmp, 0, 0);
  return c;
};

const toFile = (canvas, name) => new Promise((r) => canvas.toBlob((b) => r(new File([b], name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" })), "image/jpeg", 0.9));

// Drag the 4 corners onto the receipt edges; the result is flattened into a straight rectangle
export default function ReceiptCrop({ file, onDone }) {
  const [canvas, setCanvas] = useState(null);
  const [url, setUrl] = useState(null);
  const [pts, setPts] = useState(INSET);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const drag = useRef(null);

  useEffect(() => { toCanvas(file).then(setCanvas).catch(() => onDone(file)); }, [file]); // eslint-disable-line react-hooks/exhaustive-deps
  const [found, setFound] = useState(null);
  const auto = (c, notify) => {
    const q = detectCorners(c);
    setFound(!!q);
    setPts(q || INSET);
    if (notify) q ? toast.success("Receipt edges found — fine-tune the dots if needed") : toast("Couldn't find clear edges — drag the dots onto the corners");
  };
  useEffect(() => {
    if (!canvas) return;
    setUrl(canvas.toDataURL("image/jpeg", 0.85));
    auto(canvas, false);
  }, [canvas]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (e) => {
    if (drag.current === null || !box.current) return;
    const r = box.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    setPts((p) => p.map((q, i) => (i === drag.current ? [x, y] : q)));
  };
  const rotate = () => setCanvas(rotate90(canvas));
  const apply = async () => {
    setBusy(true);
    await new Promise((r) => setTimeout(r, 20));
    // Pull corners 1.5% toward the centre so background slivers at the edges don't confuse OCR
    const cx = pts.reduce((s, p) => s + p[0], 0) / 4, cy = pts.reduce((s, p) => s + p[1], 0) / 4;
    const out = warp(canvas, pts.map(([x, y]) => [(x + (cx - x) * 0.03) * canvas.width, (y + (cy - y) * 0.03) * canvas.height]), 1600);
    onDone(await toFile(out, file.name));
  };
  const full = async () => onDone(await toFile(canvas, file.name));

  if (!url) return <p className="text-sm text-muted-foreground">Loading photo…</p>;
  return (
    <div className="space-y-3" data-testid="receipt-crop">
      <p className="text-sm text-muted-foreground" data-testid="crop-hint">{found ? "Edges detected automatically. Fine-tune the dots if needed — the receipt will be flattened and straightened before reading." : "Drag the 4 dots onto the corners of the receipt. It will be flattened and straightened before reading."}</p>
      <div ref={box} className="relative mx-auto select-none touch-none w-fit max-w-full" onPointerMove={move} onPointerUp={() => (drag.current = null)} onPointerLeave={() => (drag.current = null)} data-testid="crop-area">
        <img src={url} alt="Receipt to crop" className="block max-h-[52vh] max-w-full rounded-lg" draggable={false} />
        <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polygon points={pts.map(([x, y]) => `${x * 100},${y * 100}`).join(" ")} fill="hsl(var(--primary) / 0.15)" stroke="hsl(var(--primary))" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
        </svg>
        {pts.map(([x, y], i) => (
          <button key={i} type="button" aria-label={`Move ${LABELS[i]} corner`} className="absolute h-7 w-7 -ml-3.5 -mt-3.5 rounded-full border-[3px] border-white bg-primary shadow-lg cursor-grab active:cursor-grabbing touch-none" style={{ left: `${x * 100}%`, top: `${y * 100}%` }} onPointerDown={(e) => { e.preventDefault(); drag.current = i; }} data-testid={`crop-handle-${i}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-2 justify-between">
        <div className="flex gap-2">
          <Button variant="outline" onClick={rotate} disabled={busy} data-testid="crop-rotate-button"><RotateCw className="h-4 w-4 mr-1" /> Rotate</Button>
          <Button variant="outline" onClick={() => auto(canvas, true)} disabled={busy} data-testid="crop-auto-button"><ScanSearch className="h-4 w-4 mr-1" /> Auto-detect</Button>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={full} disabled={busy} data-testid="crop-skip-button"><Maximize className="h-4 w-4 mr-1" /> Use full photo</Button>
          <Button onClick={apply} disabled={busy} data-testid="crop-apply-button"><Crop className="h-4 w-4 mr-1" /> {busy ? "Straightening…" : "Crop & read"}</Button>
        </div>
      </div>
    </div>
  );
}
